import { ReportTestItem } from '@/app/features/reports/utils/entities';

export interface Filter {
  matches: (item: ReportTestItem) => boolean;
}

export const createFilter = (filter: Filter) => filter;

// Base ID filter factory
const createIdFilter = <K extends keyof ReportTestItem>(
  key: K,
  id: string
): Filter => ({
  matches: (item) => item[key] === Number(id),
});

// Individual filters
export const filterByAsset = (assetId: string) =>
  createIdFilter('assetId', assetId);

export const filterByBuilding = (buildingId: string) =>
  createIdFilter('buildingId', buildingId);

export const filterByLevel = (levelId: string) =>
  createIdFilter('levelId', levelId);

export const filterByArea = (areaId: string) =>
  createIdFilter('areaId', areaId);

const parseId = (val: string | undefined): number | null => {
  if (!val || val === 'null' || val === 'undefined') return null;
  const num = Number(val);
  return isNaN(num) ? null : num;
};

const isIdMatch = (itemVal: number | null | undefined, filterVal: number | null): boolean => {
  const normalizedItem = (itemVal === undefined || itemVal === null) ? null : itemVal;
  return normalizedItem === filterVal;
};

//filterByLocation accepts a string of $buildingId-$levelId-$areaId where each part is optional
export const filterByLocation = (location: string) => {
  const parts = location.split('.');
  const ids = {
    asset: parts[0],
    building: parts[1],
    level: parts[2],
    area: parts[3],
  };

  return createFilter({
    matches: (item) => {
      const targetAreaId = parseId(ids.area);
      const targetLevelId = parseId(ids.level);
      const targetBuildingId = parseId(ids.building);
      const targetAssetId = parseId(ids.asset);

      // Check most specific (area) first
      if (ids.area) {
        if (!isIdMatch(item.areaId, targetAreaId)) {
          return false;
        }
        // If area matches, implicitly check its parent IDs
        if (
          !isIdMatch(item.levelId, targetLevelId) ||
          !isIdMatch(item.buildingId, targetBuildingId) ||
          !isIdMatch(item.assetId, targetAssetId)
        ) {
          return false;
        }
        return true;
      }

      // Check level if specified
      if (ids.level) {
        if (!isIdMatch(item.levelId, targetLevelId)) {
          return false;
        }
        // Check level's parents
        if (
          !isIdMatch(item.buildingId, targetBuildingId) ||
          !isIdMatch(item.assetId, targetAssetId)
        ) {
          return false;
        }
        return true;
      }

      // Check building if specified
      if (ids.building) {
        if (!isIdMatch(item.buildingId, targetBuildingId)) {
          return false;
        }
        // Check building's parent
        if (!isIdMatch(item.assetId, targetAssetId)) {
          return false;
        }
        return true;
      }

      // Check asset as last resort
      if (ids.asset) {
        return isIdMatch(item.assetId, targetAssetId);
      }

      return true; // No filters specified
    },
  });
};

export const createElementFilter = (facets: Set<string>): Filter => ({
  matches: (item: ReportTestItem) => {
    // Handle items with no element
    if (item.element?.path?.length < 1) {
      return facets.has('null');
    }

    const path = item.element.path;
    let currentPath = '';

    // Check from most specific to least specific
    for (let i = path.length - 1; i >= 0; i--) {
      // Build the full path from root to current level
      currentPath = path
        .slice(0, i + 1)
        .map(p => p.id)
        .join('.');

      // If this level's path is in the facets, it's a match
      if (facets.has(currentPath)) {
        return true;
      }
    }

    return false;
  },
});


///type SelectedFilters = Record<FilterTypes, string[]>;

export const applyFilters = (items: ReportTestItem[], filters: Filter[]) => {
  return items.filter((item) =>
    filters.every((filter) => filter.matches(item))
  );
};

export const createPropertyFilter = <K extends keyof ReportTestItem>(
  property: K
) => (facets: Set<string>): Filter => ({
  matches: (item: ReportTestItem) => {
    const value = String(item[property])?.toLowerCase();
    return value === undefined || value === null
      ? facets.has('null')
      : facets.has(value);
  },
});


export const createStatusFilter = createPropertyFilter('status');
export const createElementNameFilter = createPropertyFilter('element_name');
export const createElementIdFilter = createPropertyFilter('element_id');

export const createTestSuiteFilter = (suites: Set<string>): Filter => ({
  matches: (item) => {
    const { testSuiteId, category, subcategory, id: testSpecificationId, type } = item.testSpecification;
    const isCompliance = type === 'compliance';

    let matches: string[];
    
    if (isCompliance) {
      // Compliance test suites: check all 4 levels
      matches = [
        `${testSuiteId}`,
        `${testSuiteId}.${category}`,
        `${testSuiteId}.${category}.${subcategory}`,
        `${testSuiteId}.${category}.${subcategory}.${testSpecificationId}`,
      ];
    } else {
      // Non-compliance test suites: only check the flat structure
      matches = [
        `${testSuiteId}.${testSpecificationId}`,
      ];
    }
    
    return matches.some((match) => suites.has(match));
  },
});

export const createResultFilter = (results: Set<string>): Filter => ({
  matches: (item) => {
    const matches = [
      `${item.testSpecification.testSuiteId}`,
      `${item.testSpecification.testSuiteId}.${item.result}`,
    ];
    return matches.some((match) => results.has(match));
  },
});

export const createExclusionFilter = (filters: Set<string>): Filter => ({
  matches: (item) => {
    const matches = [
      `${item.switches?.include !== true ? 'manuallyexcluded' : null}`,
      `${item.status.toLowerCase() === 'draft' ? 'status.draft' : null}`,
      `${item.status.toLowerCase() === 'qa' ? 'status.qa' : null}`,
      `${item.status.toLowerCase() === 'archived' ? 'status.archived' : null}`,
      `${item.status.toLowerCase() === 'published' ? 'status.published' : null}`,
      // `${item.status.toLowerCase() !== 'published' ? 'status' : null}`,
      `${item.exclusionReason === 'status' ? 'status' : null}`,
      `${item.hasFastFillQA ? 'unmatchedsegments' : null}`,
      `${item.hasMissingData ? 'missingdata' : null}`,
      `${item.exclusionReason === 'included' ? 'included' : null}`,
    ]

    // console.debug('matches', {filters, matches});

    return matches.some((match) => filters.has(match));

  }
});

export const createCompositeFilter = (filters: Filter[]): Filter => ({
  matches: (item) => filters.every((filter) => filter.matches(item)),
});

export const createSearchFilter = (search: string): Filter => ({
  matches: (item) => {
    const searchTerm = search.toLowerCase();
    
    const toString = (value: any): string => {
      if (value === null || value === undefined) return '';
      if (value instanceof Date) return value.toISOString().toLowerCase(); 
      if (Array.isArray(value)) return value.map(toString).join(' ').toLowerCase(); 
      if (typeof value === 'object' && value !== null) {
        return ''; // Avoid deep stringify
      }
      return String(value).toLowerCase();
    };

    // Search through selected direct primitive properties
    const hasMatch = [
      item.assessmentTestId,
      item.asset_name,
      item.building_description,
      item.level_description,
      item.area_description,
      item.resultDescription,
      item.observations,
      item.recommendations,
      item.status,
    ].some(value => toString(value).includes(searchTerm));

    // Search through relevant element properties
    const hasElementMatch = item.element && (
      [
        item.element.name,
        item.element.category,
        item.element.sub_category,
        item.element.type,
        item.element.sub_type,
      ].some(value => toString(value).includes(searchTerm))
      || // Also check the names within the path array
      (item.element.path?.some(pathItem => toString(pathItem.name).includes(searchTerm)) ?? false)
    );

    // Search through test specification properties
    const hasTestSpecMatch = item.testSpecification && [
        item.testSpecification.label,
        item.testSpecification.description,
        item.testSpecification.category,
        item.testSpecification.subcategory,
        item.testSpecification.test_suite_title,
    ].some(value => toString(value).includes(searchTerm));

    // Search on customDisplayValues
    const hasCustomDisplayValueMatch = item.customDisplayValues && Object.values(item.customDisplayValues).some(value => 
      toString(value).includes(searchTerm)
    );

    return hasMatch || !!hasElementMatch || !!hasTestSpecMatch || !!hasCustomDisplayValueMatch;
  },
});
